import { createFileRoute, Link } from '@tanstack/react-router'
import { THIRD_PARTY_PUBLIC } from '@/lib/third-party'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/webhook-payloads')({
  component: WebhookPayloadsGuidePage,
  head: () => ({
    meta: [
      { title: 'Webhook Payload Reference — Uptrack' },
      { name: 'description', content: 'Exact JSON payloads Uptrack sends your webhook when a monitor goes down or recovers, signed with HMAC-SHA256. Verify and parse them in Node, Python or Elixir.' },
      { property: 'og:title', content: 'Webhook Payload Reference — Uptrack' },
      { property: 'og:description', content: 'Full reference for Uptrack uptime monitoring webhook payloads with JSON examples and code snippets.' },
      { property: 'og:image', content: 'https://uptrack.app/og/guides-webhook-payloads.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides/webhook-payloads' },
    ],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Webhook Payload Reference',
          description: 'Exact JSON payloads Uptrack sends your webhook when a monitor goes down or recovers, signed with HMAC-SHA256. Verify and parse them in Node, Python or Elixir.',
          url: 'https://uptrack.app/guides/webhook-payloads',
          datePublished: '2026-04-01',
          imageUrl: 'https://uptrack.app/og/guides-webhook-payloads.png',
        }),
      ),
    ],
  }),
})

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {title && (
        <div className="border-b border-border bg-muted/30 px-4 py-2">
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
        </div>
      )}
      <pre tabIndex={0} className="overflow-x-auto p-4 text-sm">
        <code className="font-mono text-body">{children}</code>
      </pre>
    </div>
  )
}

function FieldTable({ label, fields }: { label: string; fields: { name: string; type: string; description: string }[] }) {
  return (
    <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={label}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Field</th>
            <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Type</th>
            <th className="pb-3 text-left font-medium text-muted-foreground">Description</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, i) => (
            <tr key={field.name} className={i < fields.length - 1 ? 'border-b' : ''}>
              <td className="py-3 pr-4">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{field.name}</code>
              </td>
              <td className="py-3 pr-4 text-muted-foreground">{field.type}</td>
              <td className="py-3 text-muted-foreground">{field.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WebhookPayloadsGuidePage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            Webhook payload reference
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            When an incident happens, Uptrack sends a POST request to your webhook URL with a JSON payload.
            This page documents every field you will receive for each event type.
          </p>

          {/* Event types overview */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Event types</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Uptrack sends two types of webhook events:
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Event types">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Event</th>
                    <th className="pb-3 text-left font-medium text-muted-foreground">When it fires</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">monitor.down</code>
                    </td>
                    <td className="py-3 text-muted-foreground">A monitor fails its check and an incident is created</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">monitor.up</code>
                    </td>
                    <td className="py-3 text-muted-foreground">A monitor recovers and the incident is resolved</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* monitor.down */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              <code className="rounded bg-status-down/10 px-2 py-1 font-mono text-xl text-status-down">monitor.down</code>
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Sent when a monitor fails its health check and a new incident is created.
            </p>

            <CodeBlock title="POST https://your-server.com/webhooks/uptrack">{`{
  "event": "monitor.down",
  "timestamp": "2026-04-01T08:23:14Z",
  "monitor": {
    "id": "0192f1c4-7c8a-7000-8000-4b1d2e3f5a6c",
    "name": "Production API",
    "url": "https://api.example.com/health",
    "check_interval": 60,
    "protocol": "https"
  },
  "incident": {
    "id": "0192f1d0-3e21-7000-8000-9c8b7a6d5e4f",
    "started_at": "2026-04-01T08:23:14Z",
    "cause": "HTTP 503 Service Unavailable"
  },
  "check": null
}`}</CodeBlock>

            <div className="mt-6">
              <h3 className="font-sans mb-3 text-lg font-semibold">Field descriptions</h3>
              <FieldTable label="monitor.down fields" fields={[
                { name: 'event', type: 'string', description: 'Always "monitor.down" for this event type' },
                { name: 'timestamp', type: 'string', description: 'ISO 8601 timestamp of when the event was emitted' },
                { name: 'monitor.id', type: 'string', description: 'Unique monitor identifier' },
                { name: 'monitor.name', type: 'string', description: 'Human-readable monitor name' },
                { name: 'monitor.url', type: 'string', description: 'The URL being monitored' },
                { name: 'monitor.check_interval', type: 'integer', description: 'Check frequency in seconds (e.g. 30)' },
                { name: 'monitor.protocol', type: 'string | null', description: '"https", "http" or "tcp"; null for other monitor types' },
                { name: 'incident.id', type: 'string', description: 'Unique incident identifier' },
                { name: 'incident.started_at', type: 'string', description: 'ISO 8601 timestamp of when the incident started' },
                { name: 'incident.cause', type: 'string', description: 'Human-readable reason for the failure' },
                { name: 'check', type: 'object | null', description: 'Details of the failing check (status code, response time, location). null for now; the field is always present, so you can rely on it when it is filled in.' },
              ]} />
            </div>
          </section>

          {/* monitor.up */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              <code className="rounded bg-status-up/10 px-2 py-1 font-mono text-xl text-status-up">monitor.up</code>
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Sent when a monitor recovers and the open incident is resolved.
            </p>

            <CodeBlock title="POST https://your-server.com/webhooks/uptrack">{`{
  "event": "monitor.up",
  "timestamp": "2026-04-01T08:31:47Z",
  "monitor": {
    "id": "0192f1c4-7c8a-7000-8000-4b1d2e3f5a6c",
    "name": "Production API",
    "url": "https://api.example.com/health",
    "check_interval": 60,
    "protocol": "https"
  },
  "incident": {
    "id": "0192f1d0-3e21-7000-8000-9c8b7a6d5e4f",
    "started_at": "2026-04-01T08:23:14Z",
    "resolved_at": "2026-04-01T08:31:47Z",
    "duration_seconds": 513
  },
  "check": null
}`}</CodeBlock>

            <div className="mt-6">
              <h3 className="font-sans mb-3 text-lg font-semibold">Additional fields</h3>
              <FieldTable label="monitor.up additional fields" fields={[
                { name: 'incident.resolved_at', type: 'string', description: 'ISO 8601 timestamp of when the incident was resolved' },
                { name: 'incident.duration_seconds', type: 'integer', description: 'Total downtime duration in seconds' },
              ]} />
              <p className="mt-3 text-lg leading-[1.7] text-body">
                The <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">monitor</code> and <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">check</code> objects
                have the same shape as <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">monitor.down</code>. The <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">incident.cause</code> field
                is omitted on recovery events.
              </p>
            </div>
          </section>

          {/* monitor.third_party_unreadable */}
          {THIRD_PARTY_PUBLIC && (
            <section className="mb-12">
              <h2 className="font-sans mb-4 text-2xl font-semibold">
                <code className="rounded bg-muted px-2 py-1 font-mono text-xl text-foreground">monitor.third_party_unreadable</code>
              </h2>
              <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
                Sent once when Uptrack cannot read the status page of a service a third party monitor watches, after 15 minutes of failed reads.
                It is not an outage: Uptrack does not know whether the vendor has a problem. A good read ends the streak, and a new streak can send it again.
              </p>

              <CodeBlock title="POST https://your-server.com/webhooks/uptrack">{`{
    "event": "monitor.third_party_unreadable",
    "timestamp": "2026-04-01T08:38:15Z",
    "monitor": {
      "id": "0192f1c4-7c8a-7000-8000-4b1d2e3f5a6c",
      "name": "Cloudflare",
      "url": "https://www.cloudflarestatus.com",
      "check_interval": 300,
      "protocol": "third_party"
    },
    "incident": {
      "id": null,
      "started_at": "2026-04-01T08:23:14Z",
      "cause": "Uptrack cannot read the status page of Cloudflare since 1 April 2026 08:23 UTC. We do not know if Cloudflare has a problem. We keep trying.",
      "reason": "HTTP 503"
    },
    "check": null
  }`}</CodeBlock>

              <div className="mt-6">
                <h3 className="font-sans mb-3 text-lg font-semibold">Fields that differ</h3>
                <FieldTable label="monitor.third_party_unreadable fields" fields={[
                  { name: 'incident.id', type: 'null', description: 'Always null: no incident is opened for an unreadable page' },
                  { name: 'incident.started_at', type: 'string', description: 'ISO 8601 timestamp of when the failed reads began' },
                  { name: 'incident.cause', type: 'string', description: 'The plain message, with vendor text cleaned and cut' },
                  { name: 'incident.reason', type: 'string | null', description: 'A short reason, for example "HTTP 503". May be null' },
                  { name: 'monitor.check_interval', type: 'integer', description: 'Not meaningful for a third party monitor: Uptrack reads the page on its own schedule' },
                ]} />
              </div>
            </section>
          )}

          {/* Headers */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Request headers</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Every webhook request includes these headers:
            </p>
            <CodeBlock>{`Content-Type: application/json
User-Agent: Uptrack-Webhook/1.0
X-Uptrack-Event: monitor.down
X-Uptrack-Signature: t=1712000000,v1=7ae11423d0db7008f5a3a3195bc742a615cb29ddbddd1d096e3f52056a3ab17b`}</CodeBlock>

            <div className="mt-6">
              <FieldTable label="Request headers" fields={[
                { name: 'Content-Type', type: 'string', description: 'Always "application/json"' },
                { name: 'User-Agent', type: 'string', description: 'Always "Uptrack-Webhook/1.0"' },
                { name: 'X-Uptrack-Event', type: 'string', description: 'The event type ("monitor.down" or "monitor.up")' },
                { name: 'X-Uptrack-Signature', type: 'string', description: 't=<unix seconds>,v1=<hex HMAC-SHA256 of "<t>.<raw body>" with your signing secret>' },
              ]} />
            </div>
          </section>

          {/* Verification */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Verifying webhook requests</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Each webhook channel has its own signing secret. It starts with <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">whsec_</code>,
              and Uptrack shows it once, when you create the channel. Every request carries
              <code className="mx-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">X-Uptrack-Signature: t=…,v1=…</code>.
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">v1</code> is the hex HMAC-SHA256 of the timestamp, a dot and the raw request body.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Compute the same value with your secret, compare in constant time, and reject requests older than 5 minutes.
              Use the raw body bytes, before any JSON parsing.
            </p>
            <CodeBlock title="verify.js (Node)">{`const crypto = require('crypto')

function verifyUptrack(rawBody, header, secret, toleranceSec = 300) {
  const parts = Object.fromEntries((header || '').split(',').map((p) => p.split('=')))
  const t = Number(parts.t)
  if (!t || Math.abs(Date.now() / 1000 - t) > toleranceSec) return false
  const expected = crypto.createHmac('sha256', secret).update(\`\${t}.\`).update(rawBody).digest('hex')
  const a = Buffer.from(expected)
  const b = Buffer.from(parts.v1 || '')
  return a.length === b.length && crypto.timingSafeEqual(a, b)
}`}</CodeBlock>
            <div className="mt-4">
              <CodeBlock title="verify.py (Python)">{`import hashlib, hmac, time

def verify_uptrack(raw_body: bytes, header: str, secret: str, tolerance: int = 300) -> bool:
    parts = dict(p.split('=', 1) for p in (header or '').split(',') if '=' in p)
    t = int(parts.get('t', 0))
    if abs(time.time() - t) > tolerance:
        return False
    expected = hmac.new(secret.encode(), f"{t}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, parts.get('v1', ''))`}</CodeBlock>
            </div>
            <p className="mt-4 text-lg leading-[1.7] text-body">
              Lost the secret? Create the channel again, or rotate it with the API:
              <code className="mx-1 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">PATCH /api/alert-channels/&#123;id&#125;</code>
              with <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{'{"config": {"rotate_secret": true}}'}</code>.
              The reply carries the new secret once, and the old one stops working at once.
            </p>
          </section>

          {/* Code examples */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Code examples</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Receive and parse Uptrack webhook payloads in your language of choice.
            </p>

            {/* Node.js */}
            <div className="mb-8">
              <h3 className="font-sans mb-3 text-lg font-semibold">Node.js (Express)</h3>
              <CodeBlock title="server.js">{`const express = require('express')
const app = express()

// Keep the raw body: the signature covers the exact bytes Uptrack sent.
app.post('/webhooks/uptrack', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.get('X-Uptrack-Signature')
  if (!verifyUptrack(req.body, signature, process.env.UPTRACK_WEBHOOK_SECRET)) {
    return res.status(401).send('Bad signature')
  }
  const { event, monitor, incident } = JSON.parse(req.body)

  switch (event) {
    case 'monitor.down':
      console.log(\`[DOWN] \${monitor.name} — \${incident.cause}\`)
      // Page your on-call team, create a ticket, etc.
      break

    case 'monitor.up':
      console.log(\`[UP] \${monitor.name} — was down for \${incident.duration_seconds}s\`)
      // Resolve the ticket, notify the channel
      break

  }

  res.status(200).send('OK')
})

app.listen(3000)`}</CodeBlock>
            </div>

            {/* Python */}
            <div className="mb-8">
              <h3 className="font-sans mb-3 text-lg font-semibold">Python (Flask)</h3>
              <CodeBlock title="app.py">{`import json, os
from flask import Flask, request, jsonify

app = Flask(__name__)

@app.route('/webhooks/uptrack', methods=['POST'])
def uptrack_webhook():
    raw = request.get_data()
    if not verify_uptrack(raw, request.headers.get('X-Uptrack-Signature', ''), os.environ['UPTRACK_WEBHOOK_SECRET']):
        return jsonify(error='bad signature'), 401
    data = json.loads(raw)
    event = data['event']
    monitor = data['monitor']

    if event == 'monitor.down':
        incident = data['incident']
        print(f"[DOWN] {monitor['name']} — {incident['cause']}")

    elif event == 'monitor.up':
        incident = data['incident']
        print(f"[UP] {monitor['name']} — down for {incident['duration_seconds']}s")


    return jsonify(status='ok'), 200`}</CodeBlock>
            </div>

            {/* Elixir */}
            <div>
              <h3 className="font-sans mb-3 text-lg font-semibold">Elixir (Phoenix)</h3>
              <CodeBlock title="webhook_controller.ex">{`defmodule MyAppWeb.WebhookController do
  use MyAppWeb, :controller

  def uptrack(conn, %{"event" => "monitor.down"} = params) do
    monitor = params["monitor"]
    incident = params["incident"]
    IO.puts("[DOWN] #{monitor["name"]} — #{incident["cause"]}")

    json(conn, %{status: "ok"})
  end

  def uptrack(conn, %{"event" => "monitor.up"} = params) do
    monitor = params["monitor"]
    incident = params["incident"]
    IO.puts("[UP] #{monitor["name"]} — down for #{incident["duration_seconds"]}s")

    json(conn, %{status: "ok"})
  end
end`}</CodeBlock>
            </div>
          </section>

          {/* Testing */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Testing webhooks locally</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Use one of these tools to inspect webhook payloads before writing any code:
            </p>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-1 font-semibold">webhook.site</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Go to <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">webhook.site</code> to get a unique URL.
                  Paste it as your webhook URL in Uptrack, then trigger a test alert.
                  You will see the exact headers and payload in your browser.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-1 font-semibold">ngrok</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Run <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">ngrok http 3000</code> to
                  expose your local server. Use the generated URL as your webhook endpoint in Uptrack.
                  Requests hit your local code so you can debug with breakpoints.
                </p>
              </div>
            </div>
          </section>

          {/* Integration ideas */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Integration ideas</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Custom Slack formatting</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Parse the webhook payload and post a rich Slack message with Block Kit.
                  Include the monitor name, downtime duration, and a direct link to your status page.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">PagerDuty trigger</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Forward <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">monitor.down</code> events
                  to PagerDuty's Events API v2 to page your on-call engineer. Auto-resolve
                  when <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">monitor.up</code> arrives.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Zapier / Make</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Use a Zapier "Catch Hook" trigger to receive Uptrack webhooks, then route them to
                  Google Sheets, Jira, Linear, or any of 5,000+ apps without writing code.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Incident log database</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Store every webhook payload in a database table. Query historical incidents,
                  calculate MTTR, or build your own uptime reporting dashboard.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <h2 className="font-sans mb-3 text-xl font-semibold">Start receiving webhook alerts</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pro includes 50 monitors at 30-second checks. Start free, or choose a plan for more.
            </p>
            <Button size="lg" asChild>
              <Link to="/signup">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
